import { useEffect, useMemo } from "react"

import { useNode } from "@craftjs/core"
import { findIndex, isEmpty, nth } from "lodash";
import { ArrowBigLeft, ArrowBigRight } from "lucide-react";

import useProp from "@/modules/cms/views/post/components/builder/hooks/useProp"
import createContextFactory from "@/utils/factory"

import BasePreview, { Action } from "../../blocks/base"
import { useStateContext } from "../State";

const { Provider, useContext } = createContextFactory(({ referBlockId }) => {
  const highlighted = useProp(false)

  return useMemo(() => ({
    highlighted,
    referBlockId,
  }), [highlighted, referBlockId])
})

function HighlightProvider({ children }) {
  const { id } = useNode()

  return <Provider referBlockId={id}>
    {children}
  </Provider>
}

export function HighlightPreview({ children }) {
  const { label, options: { value: options }, show: { value, setValue } } = useStateContext()

  const selectedLabel = useMemo(() => options.find(option => option.value === value)?.label, [options, value])

  const handleToggle = direction => () => {
    if (isEmpty(options)) return
    direction = { previous: -1, next: 1 }[direction]
    const currentIndex = value ? findIndex(options, { value }) : -1
    const nextOption = nth(options, (currentIndex + direction) % options.length)

    if (nextOption?.value) {
      setValue(nextOption.value)
    }
  }
  return <HighlightProvider>
    <BasePreview label={`${label} → ${selectedLabel}`}
      customActions={<>
        <Action onClick={handleToggle('previous')}>
          <ArrowBigLeft size={12} />
        </Action>
        <Action onClick={handleToggle('next')}>
          <ArrowBigRight size={12} />
        </Action>
      </>} >
      <div>
        {children}
      </div>
    </BasePreview>
  </HighlightProvider>
}

export function useHighlight({ id, selected }) {
  const { referBlockId, highlighted } = useContext()
  const isInContext = !!referBlockId
  const isChildrenBlock = referBlockId !== id
  const isRootBlock = referBlockId === id
  const isContextChildBlock = isInContext && isChildrenBlock

  useEffect(() => {
    if (isContextChildBlock) {
      const { setValue: setHighlighted } = highlighted

      if (selected) {
        setHighlighted(true)
        return () => {
          setHighlighted(false)
        }
      }
    }
  }, [selected, isContextChildBlock, highlighted])

  if (isRootBlock) {
    const { value: highlightedValue } = highlighted
    return {
      highlighted: !!highlightedValue && !selected,
      value: !!highlightedValue,
    }
  }

  return {
    highlighted: false,
    value: false,
  }
}
